import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { localizedServerContent } from '../../../localization/presentationLanguage';
import { ZodiacCrushWorldProgressSummary } from './ZodiacCrushMapLocalizationSurface';
import useZodiacCrushMapProgress from '../../hooks/useZodiacCrushMapProgress';
import { authenticatedRequest } from '../../../services/authApi';
import { REST_API_ORIGIN } from '../../../platform/endpoints';
import { isZodiacCrushQaUnlockEnabled } from '../utils/qaUnlock';


const ZC_LEVELS_CACHE_KEY = 'zc_map_levels_cache_v1';
const ZC_LEVELS_CACHE_SLIM_KEY = 'zc_map_levels_cache_slim_v1';
const ZC_LEVELS_MIN_FULL_N = 50; // guard: never treat tiny snapshots as the full levels cache
const ZC_LEVELS_CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24h
const ZC_EXIT_TS_KEY = 'zc_const_exit_ts';

const ZC_FORCE_RELOAD_KEY = 'zc_force_reload_once_v1';

// Dev/StrictMode guard: React 18 StrictMode mounts effects twice in development.
// That can trigger duplicate levels fetches (and noisy logs) even though everything succeeds.
// We keep a short-lived throttle so the second pass skips the network call if cache/state is already good.
let __zcLevelsDevFetchGuardTs = 0;

const safeJsonParse = (raw, fallback) => {
  try { return JSON.parse(raw); } catch { return fallback; }
};

const getProcessEnv = () => {
  try {
    // `process` puede no existir en el browser => usar typeof evita ReferenceError
    return (typeof process !== 'undefined' && process && process.env) ? process.env : null;
  } catch (_) {
    return null;
  }
};

const getEnv = (key, fallback = '') => {
  const env = getProcessEnv();
  const v = env ? env[key] : undefined;
  return (v == null) ? fallback : String(v);
};

const isDevEnv = () => getEnv('NODE_ENV') !== 'production';

const clampLevel = (n, min = 1, max = 240) => {
  const v = Math.floor(Number(n || 0));
  if (!Number.isFinite(v)) return min;
  return Math.max(min, Math.min(max, v));
};

const persistLastSelectedLevel = (level) => {
  try {
    const lv = clampLevel(level, 1, 240);
    // Keep multiple legacy keys to stay compatible with older builds/screens.
    localStorage.setItem('zc_level_to_play', String(lv));
    localStorage.setItem('zc_selected_level', String(lv));
    localStorage.setItem('zc_last_level', String(lv));
    localStorage.setItem('zodiac_crush_last_level', String(lv));
  } catch (_) {}
};

const readLevelsCache = () => {
  try {
    // Read FULL first; if it's missing/expired/partial, fall back to SLIM.
    const readKey = (key, { allowSmall = false } = {}) => {
      const raw = localStorage.getItem(key) || '';
      if (!raw) return null;

      const parsed = safeJsonParse(raw, null);

      // Accept multiple cache shapes:
      // 1) { ts, levels: [...] } (current)
      // 2) { levels: [...] } (legacy)
      // 3) [...] (legacy array-only)
      // 4) { ok, levels: [...] } (accidentally cached API payload)
      const levels = Array.isArray(parsed?.levels)
        ? parsed.levels
        : (Array.isArray(parsed) ? parsed : null);

      const ts = Number(parsed?.ts || 0) || 0;

      if (!Array.isArray(levels) || levels.length === 0) return null;

      // Guard against partial/buggy writes in the FULL cache key.
      // NOTE: we allow small arrays for the SLIM key.
      if (!allowSmall && levels.length > 0 && levels.length < ZC_LEVELS_MIN_FULL_N) {
        return null;
      }

      // If we have a TTL timestamp, honor it.
      if (ts) {
        if (Date.now() - ts > ZC_LEVELS_CACHE_TTL_MS) return null;
        return levels;
      }

      // No ts (legacy): accept it once, and immediately migrate to the new FULL shape.
      try {
        // Only migrate to FULL if it looks like a full snapshot.
        if (levels.length >= ZC_LEVELS_MIN_FULL_N) {
          localStorage.setItem(ZC_LEVELS_CACHE_KEY, JSON.stringify({ ts: Date.now(), levels }));
        } else {
          localStorage.setItem(ZC_LEVELS_CACHE_SLIM_KEY, JSON.stringify({ ts: Date.now(), levels }));
        }
      } catch (_) {}

      return levels;
    };

    // 1) FULL cache (must be a reasonably complete snapshot)
    const full = readKey(ZC_LEVELS_CACHE_KEY, { allowSmall: false });
    if (Array.isArray(full) && full.length) return full;

    // 2) SLIM cache (allowed to be smaller, but ideally is a slimmed 1..240)
    const slim = readKey(ZC_LEVELS_CACHE_SLIM_KEY, { allowSmall: true });
    if (Array.isArray(slim) && slim.length) return slim;

    return null;
  } catch (_) {
    return null;
  }
};

const writeLevelsCache = (levels) => {
  try {
    if (!Array.isArray(levels) || levels.length === 0) return;

    const ts = Date.now();

    // Always try to persist a SLIM cache first so objectives are never missing.
    // This is intentionally small and should fit even when FULL cache is too large.
    try {
      const slim = levels.map((l) => ({
        level: Number(l?.level || 0) || undefined,
        worldIndex: Number(l?.worldIndex || 0) || 0,
        indexInWorld: Number(l?.indexInWorld || 0) || 0,
        sign: l?.sign,
        moves: Number(l?.moves || 0) || undefined,
        objectives: Array.isArray(l?.objectives) ? l.objectives : [],
        starThresholds: Array.isArray(l?.starThresholds) ? l.starThresholds : l?.starThresholds,
        difficulty: l?.difficulty,
      }));
      localStorage.setItem(ZC_LEVELS_CACHE_SLIM_KEY, JSON.stringify({ ts, levels: slim }));
    } catch (_) {
      // ignore
    }

    // If caller passed a tiny list (e.g., 1 level), we only keep SLIM.
    if (levels.length < ZC_LEVELS_MIN_FULL_N) return;

    // Dev log in dev: confirm cache writer is running
    try {
      if (isDevEnv()) {
  console.log('[ZC levels cache] write attempt', { n: levels.length, ts });
}
    } catch (_) {}

    // Best-effort FULL cache write.
    // IMPORTANT: do NOT remove the existing FULL key first.
    // If a FULL write fails due to quota, we want to keep the previous snapshot.
    try {
      localStorage.setItem(ZC_LEVELS_CACHE_KEY, JSON.stringify({ ts, levels }));
    } catch (_) {
      // ignore; SLIM already written
    }

    // Verify write (best-effort).
    // If FULL failed, SLIM should still exist.
    try {
      const checkFull = safeJsonParse(localStorage.getItem(ZC_LEVELS_CACHE_KEY) || '', null);
      const checkSlim = safeJsonParse(localStorage.getItem(ZC_LEVELS_CACHE_SLIM_KEY) || '', null);
      const okFull = !!(checkFull && (Array.isArray(checkFull?.levels) || Array.isArray(checkFull)));
      const okSlim = !!(checkSlim && (Array.isArray(checkSlim?.levels) || Array.isArray(checkSlim)));
      const ok = okFull || okSlim;
      if (!ok) {
        try {
          if (isDevEnv()) {
            console.warn('[ZC levels cache] write verification failed (no cache persisted)', { ts });
          }
        } catch (_) {}
      }
    } catch (_) {}
  } catch (_) {}
};

const readAuthToken = () => {
  try {
    const clean = (v) => {
      let s = String(v ?? '').trim();
      if (!s) return '';
      if (s === 'undefined' || s === 'null') return '';

      // If someone stored "Bearer <jwt>" in LS/SS, normalize.
      s = s.replace(/^Bearer\s+/i, '').trim();

      // If token was stored wrapped in quotes, unwrap.
      // Examples: "<jwt>", '<jwt>'
      if (
        s.length >= 2 &&
        ((s.startsWith('"') && s.endsWith('"')) || (s.startsWith("'") && s.endsWith("'")))
      ) {
        s = s.slice(1, -1).trim();
      }

      // Remove any stray leading/trailing quotes.
      s = s.replace(/^["']+/, '').replace(/["']+$/, '').trim();

      return s;
    };

    const getLS = (k) => { try { return clean(localStorage.getItem(k)); } catch { return ''; } };
    const getSS = (k) => { try { return clean(sessionStorage.getItem(k)); } catch { return ''; } };

    const keys = [
  'accessToken',      // 👈 CRÍTICO que vaya primero
  'token',
  'authToken',
  'zodiacus_token',
  'zodiacusToken',
  'access_token',     // 👈 extra safe variant
  'jwt',
  'jwtToken',
];

    for (const k of keys) {
      const v = getLS(k);
      if (v) return v;
    }
    for (const k of keys) {
      const v = getSS(k);
      if (v) return v;
    }
    return '';
  } catch {
    return '';
  }
};

const getApiBaseCandidates = () => {
  return [REST_API_ORIGIN];
};

async function zcGetLevels(signal) {
  const token = readAuthToken();
  const path = '/api/games/zodiac-crush/map/levels?from=1&to=240';

  // Prefer explicit backend bases first (avoids CRA/proxy issues).
  // IMPORTANT: do NOT try same-origin `/api/...` in dev by default, because CRA can return index.html
  // (or a proxy error) and it looks like a successful fetch but is not our API.
  // If you *really* want to try same-origin, enable it explicitly with localStorage: zc_try_same_origin_api=1.
  const bases = getApiBaseCandidates();
  const abs = bases.map((b) => `${b}${path}`);

  const trySameOrigin = (() => {
    try { return localStorage.getItem('zc_try_same_origin_api') === '1'; } catch (_) { return false; }
  })();

  const origin = window.location.origin;
const isLocalhost =
  origin.includes('localhost') ||
  origin.includes('127.0.0.1');

const isProd = !isLocalhost;

  // In production we ALWAYS prefer same-origin first.
  // This avoids mobile/PWA loopback/CORS issues if a stale dev override exists in localStorage.
  const candidates = isProd
    ? [path, ...abs]
    : ((trySameOrigin ? [...abs, path] : abs));

  // Dev diagnostics
  try {
    if (isDevEnv()) {
      console.log('[ZC zcGetLevels] start', {
        tokenLen: (token ? String(token).length : 0),
        candidates,
      });
    }
  } catch (_) {}

  let lastErr = null;

  for (const url of candidates) {
    try {
      const isSameOriginPath = (url === path);
      const isAbsolute = /^https?:\/\//i.test(url);
      const isSameOriginAbs = (() => {
        if (!isAbsolute) return true;
        try {
          return new URL(url).origin === window.location.origin;
        } catch (_) {
          return false;
        }
      })();
      const sendCookies = isSameOriginPath || isSameOriginAbs;

      // Dev diagnostics: log which candidate we are trying.
      try {
        if (isDevEnv()) {
          console.log('[ZC zcGetLevels] try', {
            url,
            sameOriginPath: isSameOriginPath,
            sameOriginAbs: isSameOriginAbs,
            sendCookies,
            tokenLen: token ? String(token).length : 0,
          });
        }
      } catch (_) {}

      const resp = await authenticatedRequest(url, {
        rawResponse: true,
        method: 'GET',
        signal,
        cache: 'no-store',
        headers: {
          Accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        ...(sendCookies ? { credentials: 'include' } : { credentials: 'omit', mode: 'cors' }),
        diagSource: 'ConstellationMapScreen',
      });

      // Read body once (text), then try to parse JSON from it.
      const ct = String(resp.headers.get('content-type') || '').toLowerCase();
      let txt = '';
      try { txt = await resp.text(); } catch (_) { txt = ''; }

      let data = null;
      let jsonOk = false;
      if (txt) {
        try {
          data = JSON.parse(txt);
          jsonOk = true;
        } catch (_) {
          data = null;
          jsonOk = false;
        }
      }

      try {
        if (isDevEnv()) {
          console.log('[ZC zcGetLevels] resp', {
            url,
            status: resp.status,
            contentType: ct,
            jsonOk,
            okField: data?.ok,
            bodyHead: (txt || '').slice(0, 120),
          });
        }
      } catch (_) {}

      const isJson = ct.includes('application/json') || ct.includes('application/problem+json') || jsonOk;

      // If we are calling same-origin and we didn't get JSON, assume CRA/proxy served HTML.
      // In that case, fall back to the next candidate (usually http://localhost:5001).
      if (isSameOriginPath && (!isJson || !jsonOk || !data || data.ok !== true)) {
        lastErr = new Error(`Same-origin returned non-API response (${resp.status})`);
        try {
          if (isDevEnv()) {
            console.warn('[ZC zcGetLevels] same-origin non-API, falling back', {
              url,
              status: resp.status,
              contentType: ct,
              bodyHead: (txt || '').slice(0, 120),
            });
          }
        } catch (_) {}
        continue;
      }

      // If we got an HTTP response, decide whether to fail fast or fall back to another base.
      if (!resp.ok || !data?.ok) {
        const msg = data?.message || data?.error || `Request failed (${resp.status})`;

        // If same-origin fails with common dev/proxy statuses, try the next candidate.
        const retryableStatus = [401, 403, 404, 500, 502, 503, 504];
        if (isSameOriginPath && retryableStatus.includes(Number(resp.status))) {
          lastErr = new Error(msg);
          try {
            if (isDevEnv()) {
              console.warn('[ZC zcGetLevels] same-origin failed, trying next base', { url, status: resp.status, msg });
            }
          } catch (_) {}
          continue;
        }

        // Non-same-origin auth errors should surface immediately.
        if (resp.status === 401 || resp.status === 403) throw new Error(msg || 'No autenticado');

        // Non-auth error from a real backend response -> stop.
        throw new Error(msg);
      }

      // Success
      return data;
    } catch (e) {
      // Abort should stop everything (don’t keep trying bases).
      if (String(e?.name || '').toLowerCase().includes('abort')) throw e;

      lastErr = e;

      // Network/CORS/proxy issues: try the next candidate.
      // Only retry on fetch-type errors (TypeError: Failed to fetch) or when URL is same-origin.
      const m = String(e?.message || e || '');
      const isFetchType = (e instanceof TypeError) || /failed to fetch|networkerror|load failed|fetch/i.test(m);
      if (!isFetchType) {
        // Non-network error (e.g., backend returned JSON error) -> stop.
        throw e;
      }

      // otherwise continue loop
      try {
        if (isDevEnv()) {
          console.warn('[ZC zcGetLevels] candidate failed, trying next', { url, err: m });
        }
      } catch (_) {}
    }
  }

  // All candidates failed
  const msg = localizedServerContent(String(lastErr?.message || lastErr || ''), 'The world could not be loaded');
  throw new Error(msg);
}

// Accept both ES and EN slugs for worlds/signs (keeps UI consistent across the app)
const normalizeSignKey = (raw) => {
  const k = String(raw || '').trim().toLowerCase();
  if (!k) return 'aries';

  const map = {
    aries: 'aries',
    tauro: 'taurus',
    taurus: 'taurus',
    geminis: 'gemini',
    géminis: 'gemini',
    gemini: 'gemini',
    cancer: 'cancer',
    cáncer: 'cancer',
    leo: 'leo',
    virgo: 'virgo',
    libra: 'libra',
    escorpio: 'scorpio',
    scorpio: 'scorpio',
    sagitario: 'sagittarius',
    sagittarius: 'sagittarius',
    capricornio: 'capricorn',
    capricorn: 'capricorn',
    acuario: 'aquarius',
    aquarius: 'aquarius',
    piscis: 'pisces',
    pisces: 'pisces',
  };

  return map[k] || k;
};

const SIGN_LABEL = {
  aries: 'Aries',
  taurus: 'Tauro',
  gemini: 'Géminis',
  cancer: 'Cáncer',
  leo: 'Leo',
  virgo: 'Virgo',
  libra: 'Libra',
  scorpio: 'Escorpio',
  sagittarius: 'Sagitario',
  capricorn: 'Capricornio',
  aquarius: 'Acuario',
  pisces: 'Piscis',
};

const SIGN_ELEMENT = {
  aries: 'fire',
  leo: 'fire',
  sagittarius: 'fire',

  taurus: 'earth',
  virgo: 'earth',
  capricorn: 'earth',

  gemini: 'air',
  libra: 'air',
  aquarius: 'air',

  cancer: 'water',
  scorpio: 'water',
  pisces: 'water',
};

const ELEMENT_EMOJI = {
  fire: '🔥',
  earth: '🏔️',
  air: '💨',
  water: '💧',
};

// Candy / premium element themes
const ELEMENT_THEME = {
  fire: {
    a: '#FF2D55',
    b: '#FFB300',
    glow: 'rgba(255, 45, 85, 0.55)',
    chip: 'linear-gradient(135deg, rgba(255,45,85,0.98), rgba(255,179,0,0.95))',
  },
  air: {
    a: '#34C759',
    b: '#00E5FF',
    glow: 'rgba(52, 199, 89, 0.55)',
    chip: 'linear-gradient(135deg, rgba(52,199,89,0.98), rgba(0,229,255,0.95))',
  },
  earth: {
    a: '#A86B3C',
    b: '#7A4B2A',
    glow: 'rgba(168, 107, 60, 0.55)',
    chip: 'linear-gradient(135deg, rgba(168,107,60,0.98), rgba(122,75,42,0.95))',
  },
  water: {
    a: '#00C2FF',
    b: '#6C63FF',
    glow: 'rgba(0, 194, 255, 0.55)',
    chip: 'linear-gradient(135deg, rgba(0,194,255,0.98), rgba(108,99,255,0.95))',
  },
  neutral: {
    a: '#DAA520',
    b: '#4B0082',
    glow: 'rgba(218, 165, 32, 0.45)',
    chip: 'linear-gradient(135deg, rgba(218,165,32,0.90), rgba(75,0,130,0.90))',
  },
};

const ZC_CONSTELLATION_CSS = `
  .zc-const-page{ position: relative; padding-top: calc(8px + env(safe-area-inset-top, 0px)); padding-bottom: calc(8px + env(safe-area-inset-bottom, 0px)); }

  /* background candy stars */
  .zc-const-page::before{
    content:"";
    position:absolute;
    inset:0;
    pointer-events:none;
    opacity:.55;
    background-image:
      radial-gradient(circle at 14% 18%, rgba(255,255,255,0.35) 0 1px, transparent 2px),
      radial-gradient(circle at 78% 12%, rgba(255,255,255,0.30) 0 1px, transparent 2px),
      radial-gradient(circle at 42% 76%, rgba(255,255,255,0.30) 0 1px, transparent 2px),
      radial-gradient(circle at 70% 66%, rgba(255,255,255,0.22) 0 1px, transparent 2px),
      radial-gradient(circle at 22% 62%, rgba(255,255,255,0.20) 0 1px, transparent 2px);
    mix-blend-mode: screen;
  }

  .zc-const-title{
    text-shadow: 0 2px 12px rgba(0,0,0,.45);
    letter-spacing: .9px;
  }

  .zc-const-wrap{ max-width: 720px; margin: 0 auto; }

  /* Mobile: más aire vertical para que no quede clavado arriba (iOS/Android) */
  @media (max-width: 520px){
    .zc-const-page{ padding-top: calc(22px + env(safe-area-inset-top, 0px)); }
    .zc-const-wrap{ padding-top: 10px; }
  }

  @media (max-width: 420px){
    .zc-const-page{ padding-top: calc(28px + env(safe-area-inset-top, 0px)); }
    .zc-const-wrap{ padding-top: 14px; }
  }

  .zc-const-pill{
    position: relative;
    overflow: hidden;
  }
  .zc-const-pill::after{
    content:"";
    position:absolute;
    inset:0;
    border-radius: 999px;
    background: radial-gradient(circle at 30% 20%, rgba(255,255,255,.30), transparent 60%);
    pointer-events:none;
  }

  .zc-const-node{
    position: relative;
    transform: translateZ(0);
    transition: transform 140ms ease, filter 140ms ease;
    user-select: none;
  }

  .zc-const-node::before{
    content:"";
    position:absolute;
    inset:-1px;
    border-radius: 18px;
    background:
      radial-gradient(circle at 18% 10%, rgba(255,255,255,0.28), transparent 48%),
      radial-gradient(circle at 85% 0%, rgba(255,255,255,0.12), transparent 55%),
      radial-gradient(circle at 60% 120%, rgba(0,0,0,0.22), transparent 60%);
    opacity: .95;
    pointer-events:none;
  }

  .zc-const-node::after{
    content:"";
    position:absolute;
    inset:0;
    border-radius: 16px;
    background: linear-gradient(135deg, rgba(255,255,255,0.14), rgba(255,255,255,0.02) 42%, rgba(0,0,0,0.14));
    mix-blend-mode: screen;
    opacity: .72;
    pointer-events:none;
  }

  .zc-const-node:not(.is-locked):hover{
    transform: translateY(-2px) scale(1.02);
    filter: saturate(1.10);
  }

  .zc-const-node.is-locked{
    filter: grayscale(.25) brightness(.92);
  }

  .zc-const-node.is-selected{
    outline: 2px solid rgba(255,215,0,0.70);
    box-shadow: 0 18px 44px rgba(0,0,0,.34), 0 0 0 2px rgba(255,215,0,.22);
  }

  .zc-const-mini{
    text-shadow: 0 1px 10px rgba(0,0,0,.40);
  }

  .zc-const-star{
    text-shadow: 0 2px 10px rgba(0,0,0,.35);
  }

  @media (prefers-reduced-motion: reduce){
    .zc-const-node{ transition: none; }
    .zc-const-node:not(.is-locked):hover{ transform: none; }
  }

  .zc-world-mini{
    position: relative;
    overflow:hidden;
    border-radius: 18px;
    border: 1px solid rgba(255,255,255,0.14);
    background: linear-gradient(180deg, rgba(255,255,255,0.12), rgba(0,0,0,0.10));
    box-shadow: 0 16px 40px rgba(0,0,0,0.30);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    padding: 12px 12px 10px;
    margin: 10px 0 14px;
  }
  .zc-world-mini::before{
    content:"";
    position:absolute;
    inset:-2px;
    border-radius: 20px;
    background:
      radial-gradient(circle at 15% 15%, rgba(255,255,255,0.22), transparent 50%),
      radial-gradient(circle at 85% 0%, rgba(218,165,32,0.22), transparent 58%),
      radial-gradient(circle at 70% 120%, rgba(75,0,130,0.28), transparent 60%);
    pointer-events:none;
    opacity: .95;
  }
  .zc-world-mini > *{ position: relative; }

  .zc-world-mini-top{
    display:flex;
    align-items:center;
    justify-content:space-between;
    gap: 10px;
    margin-bottom: 10px;
  }

  .zc-world-mini-title{
    font-weight: 990;
    letter-spacing: .6px;
    text-transform: uppercase;
    text-shadow: 0 2px 12px rgba(0,0,0,.45);
    font-size: 12px;
    opacity: .96;
  }

  .zc-world-mini-stats{
    display:flex;
    align-items:center;
    gap: 10px;
    font-weight: 950;
    opacity: .96;
    text-shadow: 0 2px 12px rgba(0,0,0,.35);
    font-size: 12px;
    flex-wrap: wrap;
    justify-content: flex-end;
  }

  .zc-world-bar-wrap{
    position: relative;
    overflow:hidden;
    height: 14px;
    border-radius: 999px;
    border: 1px solid rgba(255,255,255,0.16);
    background: rgba(0,0,0,0.22);
    box-shadow: inset 0 2px 10px rgba(0,0,0,0.35);
  }

  .zc-world-bar-fill{
    position:absolute;
    inset:0;
    width: 0%;
    border-radius: 999px;
    transform: translateZ(0);
  }

  .zc-world-bar-fill::after{
    content:"";
    position:absolute;
    inset:0;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.32), transparent);
    transform: translateX(-80%);
    opacity: .8;
    animation: zcWorldShine 1.9s ease-in-out infinite;
  }

  @keyframes zcWorldShine{
    0%{ transform: translateX(-80%); }
    55%{ transform: translateX(130%); }
    100%{ transform: translateX(130%); }
  }

  .zc-world-bar-text{
    margin-top: 8px;
    display:flex;
    align-items:center;
    justify-content:space-between;
    gap: 10px;
    font-weight: 950;
    opacity: .92;
    font-size: 12px;
    text-shadow: 0 2px 12px rgba(0,0,0,.35);
  }

  /* Sparkle overlay for completed nodes */
  .zc-sparkle{
    position:absolute;
    inset:0;
    border-radius: 16px;
    pointer-events:none;
    opacity: 0;
    mix-blend-mode: screen;
  }

  .zc-const-node.is-done .zc-sparkle{
    opacity: .95;
  }

  .zc-sparkle::before{
    content:"";
    position:absolute;
    inset:-20px;
    background:
      radial-gradient(circle at 18% 28%, rgba(255,255,255,0.55) 0 2px, transparent 4px),
      radial-gradient(circle at 76% 22%, rgba(255,255,255,0.42) 0 2px, transparent 4px),
      radial-gradient(circle at 42% 78%, rgba(255,255,255,0.40) 0 2px, transparent 4px),
      radial-gradient(circle at 66% 62%, rgba(255,255,255,0.32) 0 2px, transparent 4px),
      radial-gradient(circle at 30% 54%, rgba(255,255,255,0.28) 0 2px, transparent 4px);
    filter: blur(.2px);
    opacity: .75;
    animation: zcSparkleTwinkle 1.35s ease-in-out infinite;
  }

  .zc-sparkle::after{
    content:"";
    position:absolute;
    left:-60%;
    top:-80%;
    width: 60%;
    height: 260%;
    transform: rotate(18deg);
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.26), transparent);
    opacity: .0;
    animation: zcSparkleSheen 2.4s ease-in-out infinite;
  }

  @keyframes zcSparkleTwinkle{
    0%{ opacity: .55; transform: scale(1); }
    50%{ opacity: .95; transform: scale(1.01); }
    100%{ opacity: .55; transform: scale(1); }
  }

  @keyframes zcSparkleSheen{
    0%, 35%{ opacity: 0; transform: translateX(-10%) rotate(18deg); }
    55%{ opacity: .9; }
    80%{ opacity: 0; transform: translateX(220%) rotate(18deg); }
    100%{ opacity: 0; transform: translateX(220%) rotate(18deg); }
  }

  /* Subtle aura pulse for selected node */
  .zc-const-node.is-selected{
    animation: zcAuraPulse 1.85s ease-in-out infinite;
  }

  @keyframes zcAuraPulse{
    0%{ filter: saturate(1.02); }
    50%{ filter: saturate(1.14) brightness(1.03); }
    100%{ filter: saturate(1.02); }
  }

  @media (prefers-reduced-motion: reduce){
    .zc-world-bar-fill::after{ animation: none; }
    .zc-const-node.is-selected{ animation: none; }
    .zc-sparkle::before{ animation: none; }
    .zc-sparkle::after{ animation: none; }
  }
`;

export default function ConstellationMapScreen({
  worldIndex = 0,       // 0..11
  signKey = 'aries',    // string
  selectedLevel = null, // number | null (optional)
  onBack,
  onClose,
  onPlayLevel,          // ({ level }) => void
}) {
  const { t } = useTranslation('zodiacCrush');
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [levels, setLevels] = useState(() => {
    const forceReload = (() => {
      try {
        // We store the one-time reload flag in sessionStorage (it is about navigation/bfcache),
        // but keep a localStorage fallback for older builds.
        const ss = sessionStorage.getItem(ZC_FORCE_RELOAD_KEY) === '1';
        const ls = !!localStorage.getItem(ZC_FORCE_RELOAD_KEY);
        return !!(ss || ls);
      } catch (_) {
        return false;
      }
    })();

    const cached = forceReload ? null : readLevelsCache();
    return Array.isArray(cached) ? cached : [];
  });

  // If someone clears localStorage cache while this screen is already mounted,
  // React state may still have levels and we would never re-write the cache.
  // Ensure we always have a cache snapshot whenever we have levels in memory.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const debugOn = (() => {
      try { return localStorage.getItem('zc_debug_levels_cache') === '1'; } catch (_) { return false; }
    })();
    // NOTE: set localStorage zc_debug_levels_cache=1 ONLY when debugging cache issues.
    // It is intentionally noisy (poll + focus + visibility checks).

    const hasAnyLevelsCache = () => {
      try {
        const fullRaw = localStorage.getItem(ZC_LEVELS_CACHE_KEY) || '';
        const slimRaw = localStorage.getItem(ZC_LEVELS_CACHE_SLIM_KEY) || '';
        const full = fullRaw ? safeJsonParse(fullRaw, null) : null;
        const slim = slimRaw ? safeJsonParse(slimRaw, null) : null;
        const fullLvls = Array.isArray(full?.levels) ? full.levels : (Array.isArray(full) ? full : null);
        const slimLvls = Array.isArray(slim?.levels) ? slim.levels : (Array.isArray(slim) ? slim : null);
        const nFull = Array.isArray(fullLvls) ? fullLvls.length : 0;
        const nSlim = Array.isArray(slimLvls) ? slimLvls.length : 0;
        return (nFull > 0 || nSlim > 0);
      } catch (_) {
        return false;
      }
    };

    const writeUltraSlimCache = (lvls) => {
      try {
        if (!Array.isArray(lvls) || lvls.length === 0) return;
        const ts = Date.now();
        const ultra = lvls.map((l) => ({
          level: Number(l?.level || 0) || undefined,
          worldIndex: Number(l?.worldIndex || 0) || 0,
          indexInWorld: Number(l?.indexInWorld || 0) || 0,
          sign: l?.sign,
          moves: Number(l?.moves || 0) || undefined,
          // keep ONLY what the goals system needs
          objectives: Array.isArray(l?.objectives) ? l.objectives : [],
          starThresholds: Array.isArray(l?.starThresholds) ? l.starThresholds : l?.starThresholds,
          difficulty: l?.difficulty,
        }));
        localStorage.setItem(ZC_LEVELS_CACHE_SLIM_KEY, JSON.stringify({ ts, levels: ultra }));
      } catch (_) {
        // ignore
      }
    };

    const ensureCache = (reason = '') => {
      try {
        if (!Array.isArray(levels) || levels.length === 0) return;

        // If we already have either FULL or SLIM cache, do nothing.
        if (hasAnyLevelsCache()) {
          if (debugOn) console.log('[ZC levels cache] ensureCache: already present', { reason, n: levels.length });
          return;
        }

        if (debugOn) console.log('[ZC levels cache] ensureCache: missing -> seeding', { reason, n: levels.length });

        // 1) Try normal writer (FULL preferred, SLIM fallback)
        try { writeLevelsCache(levels); } catch (_) {}

        // 2) Verify. If still missing, we are likely hitting quota or a silent failure.
        if (!hasAnyLevelsCache()) {
          if (debugOn) console.warn('[ZC levels cache] ensureCache: normal write did not stick, trying ultra-slim', { reason, n: levels.length });
          writeUltraSlimCache(levels);
        }

        // 3) Final verification + breadcrumb for debugging
        if (!hasAnyLevelsCache()) {
          try { window.__zc_levels_cache_write_failed = true; } catch (_) {}
          if (debugOn) console.error('[ZC levels cache] ensureCache: FAILED to persist any cache (likely QuotaExceededError or storage blocked)');
        } else {
          try { window.__zc_levels_cache_write_failed = false; } catch (_) {}
        }
      } catch (_) {
        // ignore
      }
    };

    // Run once on mount/change
    ensureCache('effect');

    // NOTE: `storage` events do NOT fire in the same tab that calls `localStorage.removeItem`.
    // So we also keep a lightweight short-lived poll to recover immediately when a user/dev clears cache.

    // If cache is cleared in another tab/window, re-seed it.
    const onStorage = (e) => {
      try {
        const k = String(e?.key || '');
        if (k === ZC_LEVELS_CACHE_KEY || k === ZC_LEVELS_CACHE_SLIM_KEY) {
          ensureCache('storage');
        }
      } catch (_) {}
    };

    // If the tab regains focus / becomes visible, re-check cache.
    const onFocus = () => ensureCache('focus');
    const onVis = () => {
      try {
        if (document.visibilityState === 'visible') ensureCache('visibility');
      } catch (_) {}
    };

    // Short-lived poll (dev safety): if cache is missing but we already have levels in React state,
    // re-seed it. Stops automatically after ~10s.
    let pollTries = 0;
    const pollId = window.setInterval(() => {
      pollTries += 1;
      // Only run the (potentially noisy) ensureCache path if cache is missing.
      // This avoids spam when we already have FULL/SLIM cache.
      try {
        if (!hasAnyLevelsCache()) ensureCache('poll');
      } catch (_) {
        // ignore
      }
      if (pollTries >= 7) {
        try { window.clearInterval(pollId); } catch (_) {}
      }
    }, 1500);

    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);

    return () => {
      try { window.clearInterval(pollId); } catch (_) {}
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [levels]);

  // If this screen mounts before the token is persisted (common during refresh/login),
  // the first fetch can 401 and we'd never retry because the effect depends on `levels`.
  // This tick gives us a safe, low-cost re-trigger once auth becomes available.
  const [authReadyTick, setAuthReadyTick] = useState(0);

  useEffect(() => {
    let alive = true;
    let tries = 0;
    const maxTries = 60; // ~12s @ 200ms

    const poke = () => {
      if (!alive) return;
      tries += 1;
      const tok = readAuthToken();
      if (tok) {
        setAuthReadyTick((t) => t + 1);
        return;
      }
      if (tries < maxTries) {
        window.setTimeout(poke, 200);
      }
    };

    // Start immediately.
    poke();

    // Also listen for token being written in another tab/window.
    const onStorage = (e) => {
      try {
        if (!alive) return;
        const k = String(e?.key || '');
        if (
          k === 'token' ||
          k === 'authToken' ||
          k === 'accessToken' ||
          k === 'zodiacus_token' ||
          k === 'zodiacusToken'
        ) {
          setAuthReadyTick((t) => t + 1);
        }
      } catch (_) {}
    };

    window.addEventListener('storage', onStorage);
    return () => {
      alive = false;
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const goHomeFallback = useCallback(() => {
    try {
      window.location.replace('/');
    } catch (_) {
      try { window.location.replace('/'); } catch (__) {}
    }
  }, []);

  const handleExitToHome = useCallback((preferred) => {
    // Mark that we exited this screen so the next entry can ignore any bfcache/cached UI.
    try {
      const now = Date.now();
      sessionStorage.setItem(ZC_EXIT_TS_KEY, String(now));
      // When leaving to Home, request a one-time hard reload on next return (fixes iOS/Android bfcache restoring stale React state)
      sessionStorage.setItem(ZC_FORCE_RELOAD_KEY, '1');
    } catch (_) {}

    // Prefer the caller-provided handler; if none, hard-navigate to Home.
    try {
      if (typeof preferred === 'function') return preferred();
    } catch (_) {}

    goHomeFallback();
  }, [goHomeFallback]);

  const signKeyNorm = useMemo(() => normalizeSignKey(signKey), [signKey]);

  // inject CSS once
  const cssInjectedRef = useRef(false);
  const levelsRef = useRef(levels);

  // Dev/StrictMode safety: prevent stale (aborted/older) fetches from setting an error that masks valid cache.
  const _levelsReqSeqRef = useRef(0);
useEffect(() => {
    if (cssInjectedRef.current) return;
    cssInjectedRef.current = true;
    try {
      const id = 'zc_constellation_candy_css_v1';
      if (!document.getElementById(id)) {
        const tag = document.createElement('style');
        tag.id = id;
        tag.textContent = ZC_CONSTELLATION_CSS;
        document.head.appendChild(tag);
      }
    } catch (_) {}
  }, []);

  // 🔥 If the browser restores this page from bfcache, force a reload so React state never comes back stale.
  useEffect(() => {
    const onPageShow = (e) => {
      try {
        if (e?.persisted) {
          // bfcache restore: reload hard to avoid stale UI/state
          window.location.reload();
        }
      } catch (_) {}

      // Also honor our one-time forced reload flag.
      try {
        const flag = sessionStorage.getItem(ZC_FORCE_RELOAD_KEY);
        if (flag === '1') {
          sessionStorage.removeItem(ZC_FORCE_RELOAD_KEY);
          window.location.reload();
        }
      } catch (_) {}
    };

    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  const {
    progress: mapProgress,
    loading: progressLoading,
    error: progressError,
    refetch: refetchProgress,
  } = useZodiacCrushMapProgress({ auto: true });

  useEffect(() => {
    levelsRef.current = levels;
    const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    let alive = true;

    // Increment request sequence so only the latest effect run can update UI/error.
    const seq = (_levelsReqSeqRef.current = (_levelsReqSeqRef.current || 0) + 1);

    // Dev/StrictMode: avoid immediate duplicate fetch storms.
    // If we very recently started a levels fetch, and we already have cache/state to render,
    // skip this run (the other StrictMode pass will finish and populate state/cache).
    if (isDevEnv()) {
      try {
        const now = Date.now();
        const recentlyStarted = (__zcLevelsDevFetchGuardTs && (now - __zcLevelsDevFetchGuardTs) < 650);
        const stateHas = Array.isArray(levelsRef.current) && levelsRef.current.length > 0;
        const cacheHas = (() => {
          try {
            const c = readLevelsCache();
            return Array.isArray(c) && c.length > 0;
          } catch (_) {
            return false;
          }
        })();

        if (recentlyStarted && (stateHas || cacheHas)) {
          // Keep UI non-blocking.
          try { setErr(''); } catch (_) {}
          try { setLoading(false); } catch (_) {}
          return;
        }

        __zcLevelsDevFetchGuardTs = now;
      } catch (_) {
        // ignore
      }
    }

    // If we are returning after an exit to Home (or a bfcache restore), ignore cached levels once.
    // This avoids "descajete" (stale UI) after leaving this screen via Volver/✕.
    try {
      const ts = Number(sessionStorage.getItem(ZC_EXIT_TS_KEY) || 0) || 0;
      if (ts && (Date.now() - ts) < 10 * 60 * 1000) {
        // Drop cached levels so we re-fetch a clean snapshot.
        setLevels([]);
        try { sessionStorage.removeItem(ZC_EXIT_TS_KEY); } catch (_) {}
      }
    } catch (_) {}

    // Hydrate from cache (if present) to avoid blank UI and to avoid surfacing transient fetch errors.
    // IMPORTANT: do NOT early-return here; we still want a background refresh when auth is ready.
    try {
      const cachedLvls = readLevelsCache();
      if (Array.isArray(cachedLvls) && cachedLvls.length > 0) {
        setLevels((prev) => (Array.isArray(prev) && prev.length > 0 ? prev : cachedLvls));
        try { setErr(''); } catch (_) {}
        // If we were showing a spinner and cache exists, we can stop blocking the UI.
        if (_levelsReqSeqRef.current === seq) {
          try { setLoading(false); } catch (_) {}
        }
      }
    } catch (_) {}

    (async () => {
      try {
        if (_levelsReqSeqRef.current === seq) {
          setLoading(true);
          setErr('');
        }

        // If auth isn't ready yet, don't burn a 401 and get stuck; we'll retry via authReadyTick.
        const tok = readAuthToken();
        if (!tok) {
          // Auth not ready yet; wait for `authReadyTick`.
          // If we already have cache/state, don't force a blocking spinner.
          if (_levelsReqSeqRef.current === seq) {
            try { setErr(''); } catch (_) {}
            const hasState = Array.isArray(levelsRef.current) && levelsRef.current.length > 0;
            const hasCache = (() => {
              try {
                const c = readLevelsCache();
                return Array.isArray(c) && c.length > 0;
              } catch (_) {
                return false;
              }
            })();
            if (!hasState && !hasCache) {
              try { setLoading(true); } catch (_) {}
            }
          }
          return;
        }

        const lvlRes = await zcGetLevels(ctrl?.signal);
        if (!alive) return;
        if (_levelsReqSeqRef.current !== seq) return;

        const nextLevels = Array.isArray(lvlRes.levels) ? lvlRes.levels : [];

        // Write cache first so other screens (and the same session) can immediately read objectives.
        writeLevelsCache(nextLevels);

        if (_levelsReqSeqRef.current === seq) {
          setLevels(nextLevels);
        }

        // Ensure any previous transient error is cleared on successful fetch.
        try { setErr(''); } catch (_) {}

        try { sessionStorage.removeItem(ZC_FORCE_RELOAD_KEY); } catch (_) {}
        try { localStorage.removeItem(ZC_FORCE_RELOAD_KEY); } catch (_) {}

        // Best-effort: write again after state update.
        writeLevelsCache(nextLevels);
      } catch (e) {
        if (!alive) return;
        if (_levelsReqSeqRef.current !== seq) return;
        if (String(e?.name || '').toLowerCase().includes('abort')) return;

        // Always try to hydrate from cache/state first; most failures here are transient (StrictMode/abort/race).
        let hydrated = false;
        try {
          const cachedLvls = readLevelsCache();
          if (Array.isArray(cachedLvls) && cachedLvls.length > 0) {
            setLevels((prev) => (Array.isArray(prev) && prev.length > 0 ? prev : cachedLvls));
            try { setErr(''); } catch (_) {}
            hydrated = true;
          }
        } catch (_) {
          // ignore
        }

        const stateHasLevels = Array.isArray(levelsRef.current) && levelsRef.current.length > 0;
        if (hydrated || stateHasLevels) {
          // We have something to show; don't surface a blocking error.
          try { setErr(''); } catch (_) {}
          return;
        }

        const msg = String(
          e?.message ||
          e ||
          `No se pudo cargar Recuperar: GET "${getApiBaseCandidates()?.[0] || 'http://localhost:5001'}${'/api/games/zodiac-crush/map/levels?from=1&to=240'}".`
        );

        // Dev/StrictMode: if a newer request has started, do not surface this error.
        if (isDevEnv() && _levelsReqSeqRef.current !== seq) return;

        try {
          if (isDevEnv()) {
            console.warn('[ZC ConstellationMap] load levels failed', { msg, err: String(e?.message || e || '') });
          }
        } catch (_) {}

        // Dev/StrictMode: avoid flashing a scary error if a second effect-run succeeds right after.
        // Delay error slightly; if levels/cache appear, we suppress the error.
        if (isDevEnv()) {
          try {
            window.setTimeout(() => {
              try {
                if (!alive) return;
                if (_levelsReqSeqRef.current !== seq) return;

                const stateHas = Array.isArray(levelsRef.current) && levelsRef.current.length > 0;
                const cacheHas = (() => {
                  try {
                    const c = readLevelsCache();
                    return Array.isArray(c) && c.length > 0;
                  } catch (_) {
                    return false;
                  }
                })();

                if (stateHas || cacheHas) {
                  try { setErr(''); } catch (_) {}
                  return;
                }

                setErr(msg);
              } catch (_) {
                // ignore
              }
            }, 450);
          } catch (_) {
            // ignore
          }
          return;
        }

        setErr(msg);
      } finally {
        if (!alive) return;
        if (_levelsReqSeqRef.current === seq) setLoading(false);
      }
    })().catch(() => {
      // React dev/StrictMode can mount/unmount effects rapidly; ensure we never surface
      // an unhandled promise rejection even if some edge-case slips past try/catch.
    });

    return () => {
      alive = false;
      // In React dev StrictMode, effects mount/unmount twice. Aborting the first fetch can
      // produce noisy "No se pudo cargar..." console messages even though the next run succeeds.
      // We rely on `alive`/`seq` guards to ignore stale results, so we only abort in non-dev.
      try {
        if (!isDevEnv()) ctrl?.abort();
      } catch (_) {}
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authReadyTick]);

  const worldLevels = useMemo(() => {
    const all = Array.isArray(levels) ? levels : [];
    const list = all.filter((l) => Number(l.worldIndex) === Number(worldIndex));
    return list.sort((a, b) => (a.indexInWorld || 0) - (b.indexInWorld || 0));
  }, [levels, worldIndex]);

  const starsByLevel = useMemo(() => {
    const apiSrc = mapProgress?.starsByLevel;
    return (apiSrc && typeof apiSrc === 'object') ? apiSrc : {};
  }, [mapProgress?.starsByLevel]);

  const maxUnlocked = useMemo(() => {
    const apiMax = Number(mapProgress?.maxUnlockedLevel);
    const apiCur = Number(mapProgress?.currentLevel);
    const a = (Number.isFinite(apiMax) && apiMax > 0) ? apiMax : 1;
    const b = (Number.isFinite(apiCur) && apiCur > 0) ? apiCur : 1;

    // The unlock truth is the furthest reachable: max(currentLevel, maxUnlockedLevel)
    return Math.max(a, b);
  }, [
    mapProgress?.maxUnlockedLevel,
    mapProgress?.currentLevel,
  ]);

  // Local QA convenience only. The shared helper is hard-disabled in production.
  const qaUnlockAll = useMemo(() => isZodiacCrushQaUnlockEnabled(), []);

  const effectiveMaxUnlocked = qaUnlockAll ? 9999 : Math.max(1, Number(maxUnlocked) || 1);

  const selectedLevelNum = Number(selectedLevel || 0) || 0;

  const firstUnlockedInWorld = useMemo(() => {
    for (const l of worldLevels) {
      const levelNum = Number(l.level);
      if (Number.isFinite(levelNum) && levelNum > 0 && levelNum <= effectiveMaxUnlocked) return levelNum;
    }
    return null;
  }, [worldLevels, effectiveMaxUnlocked]);

  const selectedIsInWorld = useMemo(() => {
    if (!selectedLevelNum) return false;
    return worldLevels.some((l) => Number(l.level) === selectedLevelNum);
  }, [worldLevels, selectedLevelNum]);

  const selectedIsLocked = useMemo(() => {
    if (!selectedLevelNum || !selectedIsInWorld) return true;
    return selectedLevelNum > effectiveMaxUnlocked;
  }, [selectedLevelNum, selectedIsInWorld, effectiveMaxUnlocked]);

  const ctaLevelToPlay = (!selectedIsLocked && selectedIsInWorld)
    ? selectedLevelNum
    : firstUnlockedInWorld;

  const title = t(`signs.${signKeyNorm}`, { defaultValue: SIGN_LABEL[signKeyNorm] || t('worldNumber', { number: Number(worldIndex) + 1 }) });

  const worldElement = SIGN_ELEMENT[String(signKeyNorm || 'aries')] || 'neutral';
  const elTheme = ELEMENT_THEME[worldElement] || ELEMENT_THEME.neutral;

  // Mobile layout helpers (avoid content getting stuck under notch / too high)
  const isMobile520 = useMemo(() => {
    try {
      return typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 520px)').matches;
    } catch (_) {
      return false;
    }
  }, []);

  const isMobile420 = useMemo(() => {
    try {
      return typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 420px)').matches;
    } catch (_) {
      return false;
    }
  }, []);

  const pageTopPad = isMobile420
    ? 'calc(46px + env(safe-area-inset-top, 0px))'
    : isMobile520
      ? 'calc(38px + env(safe-area-inset-top, 0px))'
      : 'calc(18px + env(safe-area-inset-top, 0px))';

  const pageBottomPad = 'calc(18px + env(safe-area-inset-bottom, 0px))';

  const totalWorldStars = useMemo(() => {
    let sum = 0;
    for (const l of worldLevels) {
      const levelNum = Number(l.level);
      const s = Number(starsByLevel[String(levelNum)] || 0);
      if (s > 0) sum += Math.min(3, Math.max(0, s));
    }
    return sum;
  }, [worldLevels, starsByLevel]);

  const worldMaxStars = useMemo(() => {
    const n = Array.isArray(worldLevels) ? worldLevels.length : 0;
    return Math.max(0, n) * 3;
  }, [worldLevels]);

  const worldStarPct = useMemo(() => {
    const max = Number(worldMaxStars) || 0;
    if (max <= 0) return 0;
    return Math.max(0, Math.min(100, Math.round((Number(totalWorldStars) / max) * 100)));
  }, [totalWorldStars, worldMaxStars]);

  const starText = (s) => (s >= 3 ? '⭐⭐⭐' : s === 2 ? '⭐⭐' : s === 1 ? '⭐' : '');

  const styles = useMemo(() => ({
    page: {
      minHeight: '100dvh',
      paddingTop: pageTopPad,
      paddingRight: 14,
      paddingBottom: pageBottomPad,
      paddingLeft: 14,
      color: 'rgba(255,255,255,0.94)',
      background:
        'radial-gradient(circle at 18% -10%, rgba(255,255,255,0.14), transparent 45%),' +
        `radial-gradient(circle at 0% 18%, ${elTheme.glow}, transparent 55%),` +
        'radial-gradient(circle at 85% 0%, rgba(218,165,32,0.22), transparent 55%),' +
        'radial-gradient(circle at 80% 110%, rgba(75,0,130,0.34), transparent 60%),' +
        'linear-gradient(180deg, rgba(9,6,16,1), rgba(10,8,18,1))',
    },
    gridWrap: { maxWidth: 720, margin: '0 auto', width: '100%', paddingTop: isMobile520 ? 8 : 0 },
    top: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 14 },
    btn: {
      padding: '9px 12px',
      borderRadius: 14,
      border: '1px solid rgba(255,255,255,0.18)',
      background: 'linear-gradient(180deg, rgba(255,255,255,0.16), rgba(0,0,0,0.10))',
      color: 'rgba(255,255,255,0.94)',
      fontWeight: 950,
      cursor: 'pointer',
      boxShadow: '0 14px 26px rgba(0,0,0,0.28)',
      backdropFilter: 'blur(10px)',
      WebkitBackdropFilter: 'blur(10px)',
    },
    btnIcon: {
      padding: '9px 12px',
      borderRadius: 14,
      border: '1px solid rgba(255,255,255,0.18)',
      background: 'linear-gradient(180deg, rgba(255,255,255,0.14), rgba(0,0,0,0.14))',
      color: 'rgba(255,255,255,0.94)',
      fontWeight: 980,
      cursor: 'pointer',
      boxShadow: '0 14px 26px rgba(0,0,0,0.28)',
      lineHeight: 1,
      backdropFilter: 'blur(10px)',
      WebkitBackdropFilter: 'blur(10px)',
    },
    ctaBtn: {
      padding: '9px 12px',
      borderRadius: 14,
      border: 'none',
      background: `linear-gradient(135deg, ${elTheme.a}, ${elTheme.b})`,
      color: 'rgba(15,10,22,0.92)',
      fontWeight: 990,
      cursor: 'pointer',
      boxShadow: `0 16px 32px rgba(0,0,0,0.34), 0 0 0 2px ${elTheme.glow}`,
      lineHeight: 1,
      textShadow: '0 1px 10px rgba(255,255,255,0.18)',
    },
    ctaBtnDisabled: {
      background: 'rgba(255,255,255,0.08)',
      color: 'rgba(255,255,255,0.55)',
      border: '1px solid rgba(255,255,255,0.14)',
      cursor: 'not-allowed',
      boxShadow: 'none',
      textShadow: 'none',
    },
    badge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 8,
      padding: '7px 10px',
      borderRadius: 999,
      border: '1px solid rgba(255,255,255,0.16)',
      background: 'linear-gradient(180deg, rgba(255,255,255,0.16), rgba(0,0,0,0.06))',
      boxShadow: '0 12px 24px rgba(0,0,0,0.22)',
      fontWeight: 950,
      fontSize: 12,
      letterSpacing: 0.5,
      textTransform: 'uppercase',
      backdropFilter: 'blur(10px)',
      WebkitBackdropFilter: 'blur(10px)',
      textShadow: '0 2px 10px rgba(0,0,0,0.25)',
    },
    title: { margin: 0, fontWeight: 990, letterSpacing: 0.9, textTransform: 'uppercase' },
    grid: {
      display: 'grid',
      gridTemplateColumns:
        typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 420px)').matches
          ? 'repeat(4, minmax(0, 1fr))'
          : 'repeat(5, minmax(0, 1fr))',
      gap: 10,
    },
    node: {
      height: 58,
      borderRadius: 16,
      display: 'grid',
      placeItems: 'center',
      background: elTheme.chip,
      border: '1px solid rgba(255,255,255,0.18)',
      boxShadow: `0 16px 36px rgba(0,0,0,0.30), 0 0 0 2px ${elTheme.glow}`,
      cursor: 'pointer',
      fontWeight: 980,
      color: 'rgba(255,255,255,0.96)',
    },
    locked: { cursor: 'not-allowed', opacity: 0.55 },
    done: {
      background: `linear-gradient(135deg, rgba(255,255,255,0.10), rgba(255,255,255,0.02)), ${elTheme.chip}`,
      border: '1px solid rgba(255,255,255,0.22)',
    },
    nodeSelected: {
      outline: '2px solid rgba(255,215,0,0.65)',
      boxShadow: '0 18px 44px rgba(0,0,0,0.34), 0 0 0 2px rgba(255,215,0,0.22)',
      transform: 'translateY(-1px)',
    },
    errorBox: {
      marginTop: 12,
      padding: '12px 12px',
      borderRadius: 16,
      background: 'rgba(255,80,80,0.12)',
      border: '1px solid rgba(255,80,80,0.22)',
      color: 'rgba(255,255,255,0.92)',
      fontWeight: 900,
      boxShadow: '0 16px 30px rgba(0,0,0,0.24)',
      backdropFilter: 'blur(10px)',
      WebkitBackdropFilter: 'blur(10px)',
    },
    loader: {
      marginTop: 12,
      padding: '12px 12px',
      borderRadius: 16,
      background: 'rgba(255,255,255,0.08)',
      border: '1px solid rgba(255,255,255,0.14)',
      fontWeight: 900,
      boxShadow: '0 16px 30px rgba(0,0,0,0.22)',
      backdropFilter: 'blur(10px)',
      WebkitBackdropFilter: 'blur(10px)',
    },
    miniHeader: {
      marginTop: 10,
      marginBottom: 14,
    },
    miniTitleRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 10,
      marginBottom: 10,
    },
    miniTitle: {
      fontWeight: 990,
      letterSpacing: 0.7,
      textTransform: 'uppercase',
      fontSize: 12,
      opacity: 0.96,
      textShadow: '0 2px 12px rgba(0,0,0,0.45)',
    },
    miniStats: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 10,
      flexWrap: 'wrap',
      justifyContent: 'flex-end',
      fontWeight: 950,
      fontSize: 12,
      opacity: 0.96,
      textShadow: '0 2px 12px rgba(0,0,0,0.35)',
    },
    miniBarFill: {
      height: '100%',
      borderRadius: 999,
      background: `linear-gradient(90deg, ${elTheme.a}, ${elTheme.b})`,
      boxShadow: `0 10px 20px rgba(0,0,0,0.18), 0 0 0 2px ${elTheme.glow}`,
    },
  }), [elTheme.a, elTheme.b, elTheme.chip, elTheme.glow, pageTopPad, pageBottomPad, isMobile520]);

  return (
    <div style={styles.page} className="zc-const-page">
      <div style={styles.gridWrap} className="zc-const-wrap">
        <div style={styles.top}>
          <button
            style={styles.btn}
            onClick={() => handleExitToHome(onBack)}
          >
            ← {t('back')}
          </button>

          <h3 style={styles.title} className="zc-const-title">{title}</h3>

          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <button
              type="button"
              style={{
                ...styles.ctaBtn,
                ...(ctaLevelToPlay ? null : styles.ctaBtnDisabled),
              }}
              disabled={!ctaLevelToPlay}
              onClick={() => {
                if (!ctaLevelToPlay) return;
                try { persistLastSelectedLevel(ctaLevelToPlay); } catch (_) {}
                if (typeof onPlayLevel === 'function') onPlayLevel({ level: ctaLevelToPlay });
              }}
              aria-label={t('playNextLevel')}
              title={ctaLevelToPlay ? t('playLevel', { level: ctaLevelToPlay }) : t('noUnlockedLevels')}
            >
              ▶️ {t('play')} {ctaLevelToPlay ? `#${ctaLevelToPlay}` : ''}
            </button>

            <div style={styles.badge} className="zc-const-pill" title={t('worldElement')}>
              {ELEMENT_EMOJI[worldElement] || '✨'} {t(`elements.${worldElement}`, { defaultValue: worldElement === 'neutral' ? 'Neutral' : worldElement })}
            </div>

            <div
              style={styles.badge}
              className="zc-const-pill"
              title={qaUnlockAll ? 'QA: unlockAll' : t('maximumUnlockedLevel')}
            >
              🔓 {qaUnlockAll ? 'QA' : maxUnlocked}
            </div>

            <button
              type="button"
              style={styles.btnIcon}
              onClick={() => {
                try { if (typeof refetchProgress === 'function') refetchProgress({ force: true }); } catch (_) {}
              }}
              aria-label={t('refresh')}
              title={t('refresh')}
            >
              ⟳
            </button>

            <button
              type="button"
              style={styles.btnIcon}
              onClick={() => handleExitToHome(onClose || onBack)}
              aria-label={t('close')}
              title={t('close')}
            >
              ✕
            </button>
          </div>
        </div>

        <div style={styles.miniHeader} className="zc-world-mini">
          <div style={styles.miniTitleRow} className="zc-world-mini-top">
            <ZodiacCrushWorldProgressSummary
              totalWorldStars={totalWorldStars}
              worldMaxStars={worldMaxStars}
              levelCount={worldLevels.length}
              percent={worldStarPct}
              titleStyle={styles.miniTitle}
              titleClassName="zc-world-mini-title"
              statsStyle={styles.miniStats}
              statsClassName="zc-world-mini-stats"
              percentStyle={{ fontWeight: 990, opacity: 0.96 }}
            />
          </div>

          <div className="zc-world-bar-wrap" role="progressbar" aria-valuenow={worldStarPct} aria-valuemin={0} aria-valuemax={100}>
            <div
              className="zc-world-bar-fill"
              style={{
                ...styles.miniBarFill,
                width: `${worldStarPct}%`,
              }}
            />
          </div>

          <div className="zc-world-bar-text">
            <span style={{ opacity: 0.92 }}>{t('completeLevelsForStars')}</span>
            <span style={{ opacity: 0.92 }}>
              {worldStarPct < 100 ? t('starsRemaining', { count: Math.max(0, worldMaxStars - totalWorldStars) }) : t('worldCompleted')}
            </span>
          </div>
        </div>

        {loading || progressLoading ? (
          <div style={styles.loader}>{t('loadingLevels')}</div>
        ) : (Array.isArray(worldLevels) && worldLevels.length > 0) ? (
          <div style={styles.grid}>
            {worldLevels.map((l) => {
              const levelNum = Number(l.level);
              const levelNumValid = Number.isFinite(levelNum) && levelNum > 0;
              const stars = levelNumValid ? Number(starsByLevel[String(levelNum)] || 0) : 0;
              // If the level number is invalid, treat as locked so we never accidentally unlock everything.
              const locked = !levelNumValid || levelNum > effectiveMaxUnlocked;
              const isSelected = levelNumValid && Number(selectedLevel) === levelNum;

              const key = (l && (l.id || l._id)) ? (l.id || l._id) : `${Number(l.worldIndex) || 0}-${Number(l.indexInWorld) || 0}-${String(l.level ?? '')}`;

              return (
                <div
                  key={key}
                  className={`zc-const-node ${locked ? 'is-locked' : ''} ${stars > 0 ? 'is-done' : ''} ${isSelected ? 'is-selected' : ''}`}
                  style={{
                    ...styles.node,
                    ...(locked ? styles.locked : null),
                    ...(stars > 0 ? styles.done : null),
                    ...(isSelected ? styles.nodeSelected : null),
                  }}
                  onClick={() => {
                    if (locked) return;
                    if (!levelNumValid) return;
                    try { persistLastSelectedLevel(levelNum); } catch (_) {}
                    if (typeof onPlayLevel === 'function') onPlayLevel({ level: levelNum });
                    else alert(t('playLevel', { level: levelNum }));
                  }}
                  title={
                    !levelNumValid
                      ? t('invalidLevel')
                      : locked
                        ? t('locked')
                        : stars > 0
                          ? t('completedWithStars', { stars })
                          : t('available')
                  }
                >
                  <div className="zc-sparkle" aria-hidden="true" />
                  <div style={{ display: 'grid', gap: 4, placeItems: 'center', position: 'relative' }}>
                    <div className="zc-const-mini">#{l.indexInWorld || levelNum}</div>
                    <div className="zc-const-star" style={{ fontSize: 12, opacity: 0.95 }}>
                      {locked ? '🔒' : starText(stars)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : err || progressError ? (
          <div style={styles.errorBox}>⚠️ {err || progressError}</div>
        ) : (
          <div style={styles.loader}>{t('loadingLevels')}</div>
        )}
      </div>
    </div>
  );
}
